/**
 * Per-document verification chip for the six prose documents (inc6 step E,
 * approved gate class: available-with-warning). A VIEWER of the latest
 * pipeline run's M-checks — honest about the last run, never a block:
 *   issues     → amber, count of open drift/stale-name findings
 *   unverified → gray, the doc predates manifests (regenerate to gain coverage)
 *   clean      → green, checked against the study
 *   unknown    → the pre-coverage label, verbatim
 */
import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { db } from '../../firebase';
import {
  proseDocStatus,
  type RunCheckItemsLike,
} from '../../features/verification/proseDocStatus';

export default function ProseDocChip({ projectId, docKey }: { projectId: string; docKey: string }) {
  const [runId, setRunId] = useState<string | null>(null);
  const [checks, setChecks] = useState<RunCheckItemsLike[] | null>(null);

  useEffect(() => {
    if (!projectId) return;
    const unsub = onSnapshot(
      doc(db, 'projects', projectId, 'pipelineVerification', 'latest'),
      (snap) => setRunId(snap.exists() ? String(snap.data().runId ?? '') || null : null),
      () => setRunId(null),
    );
    return () => unsub();
  }, [projectId]);

  useEffect(() => {
    if (!projectId || !runId) {
      setChecks(null);
      return;
    }
    const unsub = onSnapshot(
      doc(db, 'projects', projectId, 'pipelineVerificationRuns', runId),
      (snap) => setChecks(snap.exists() ? ((snap.data().checks ?? []) as RunCheckItemsLike[]) : null),
      () => setChecks(null),
    );
    return () => unsub();
  }, [projectId, runId]);

  const { status, count } = proseDocStatus(checks, docKey);

  if (status === 'unknown') {
    return (
      <p className="text-[11px] text-gray-400 mt-1">Not yet covered by cross-document verification</p>
    );
  }
  if (status === 'unverified') {
    return (
      <p className="text-[11px] text-gray-400 mt-1">
        Not yet verified — regenerate to gain drift coverage
      </p>
    );
  }
  if (status === 'issues') {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 ring-1 ring-amber-600/20 mt-1">
        <AlertTriangle className="h-3 w-3" />
        {count} item{count === 1 ? '' : 's'} to review
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-600/20 mt-1">
      <CheckCircle2 className="h-3 w-3" />
      Checked against the study
    </span>
  );
}
